import { ColorV2 } from "config/colors";
import type { CreditDeposit, CreditWithdrawal } from "model/TipCredit";
import { companyCredits } from "services/api/company/companyCredits.service";
import type {
  TransactionItem,
  TransactionPageResult,
  TransactionSummaryFilter,
  TransactionSummaryModel,
} from "./tips.transactions.types";

type Translate = (key: string, options?: Record<string, string>) => string;

interface FetchTransactionPageParams {
  readonly companyId: number;
  readonly page: number;
}

interface GetReferenceLabelParams {
  readonly transaction: TransactionItem;
}

interface GetStatusLabelParams {
  readonly status: string;
  readonly t: Translate;
}

interface TransactionFilterParams {
  readonly filter: TransactionSummaryFilter;
  readonly transactions: readonly TransactionItem[];
}

interface TransactionMergeParams {
  readonly currentTransactions: readonly TransactionItem[];
  readonly nextTransactions: readonly TransactionItem[];
}

interface TransactionSortParams {
  readonly transactions: readonly TransactionItem[];
}

const TRANSACTIONS_PAGE_SIZE = 100;

export function buildSummary({
  transactions,
}: TransactionSortParams): TransactionSummaryModel {
  const allTransactionsAmount = transactions.reduce(
    (sum, transaction) => sum + transaction.amount,
    0,
  );
  const depositAmount = transactions
    .filter((transaction) => transaction.type === "deposit")
    .reduce((sum, transaction) => sum + transaction.amount, 0);
  const paidSwishAmount = transactions
    .filter((transaction) => transaction.type === "withdrawal")
    .reduce((sum, transaction) => sum + transaction.amount, 0);
  const withdrawalAmount = paidSwishAmount;

  return {
    allTransactionsAmount,
    depositAmount,
    // TODO(tips-transactions): Split bank and Swish withdrawals by sourceType when that API contract is ready.
    paidBankAmount: 0,
    paidSwishAmount,
    withdrawalAmount,
  };
}

export function filterTransactionsBySummaryFilter({
  filter,
  transactions,
}: TransactionFilterParams): TransactionItem[] {
  if (filter === "all") return [...transactions];
  if (filter === "deposit") {
    return transactions.filter((transaction) => transaction.type === "deposit");
  }
  if (filter === "withdrawal") {
    return transactions.filter(
      (transaction) => transaction.type === "withdrawal",
    );
  }
  if (filter === "swish") {
    return transactions.filter(
      (transaction) => transaction.type === "withdrawal",
    );
  }

  return [];
}

export function toTransactionKey(transaction: TransactionItem): string {
  return `${transaction.type}-${transaction.id}`;
}

export function mergeTransactionPages({
  currentTransactions,
  nextTransactions,
}: TransactionMergeParams): TransactionItem[] {
  const transactionsByKey = new Map<string, TransactionItem>();

  currentTransactions.forEach((transaction) => {
    transactionsByKey.set(toTransactionKey(transaction), transaction);
  });
  nextTransactions.forEach((transaction) => {
    transactionsByKey.set(toTransactionKey(transaction), transaction);
  });

  return Array.from(transactionsByKey.values());
}

function toDepositTransactions(
  deposits: readonly CreditDeposit[],
): TransactionItem[] {
  return deposits.map((deposit) => ({
    amount: deposit.amount,
    createdAt: deposit.createdAt,
    id: deposit.id,
    reference: deposit.reference,
    status: deposit.status,
    type: "deposit" as const,
  }));
}

function toWithdrawalTransactions(
  withdrawals: readonly CreditWithdrawal[],
): TransactionItem[] {
  return withdrawals.map((withdrawal) => ({
    amount: withdrawal.amount,
    createdAt: withdrawal.createdAt,
    id: withdrawal.id,
    reference: withdrawal.reference,
    status: withdrawal.status,
    type: "withdrawal" as const,
  }));
}

export async function fetchDepositTransactionPage({
  companyId,
  page,
}: FetchTransactionPageParams): Promise<TransactionPageResult> {
  const response = await companyCredits.getCreditDeposits(companyId, {
    page,
    pageSize: TRANSACTIONS_PAGE_SIZE,
  });

  const nextPage = page + 1;

  return {
    hasMore: nextPage * TRANSACTIONS_PAGE_SIZE < response.data.total,
    source: "deposit",
    transactions: toDepositTransactions(response.data.deposits),
  };
}

export async function fetchWithdrawalTransactionPage({
  companyId,
  page,
}: FetchTransactionPageParams): Promise<TransactionPageResult> {
  const response = await companyCredits.getCreditWithdrawals(companyId, {
    page,
    pageSize: TRANSACTIONS_PAGE_SIZE,
  });

  const nextPage = page + 1;

  return {
    hasMore: nextPage * TRANSACTIONS_PAGE_SIZE < response.data.total,
    source: "withdrawal",
    transactions: toWithdrawalTransactions(response.data.withdrawals),
  };
}

export function getStatusLabel({ status, t }: GetStatusLabelParams): string {
  const statusKeyByStatus: Record<string, string> = {
    Approved: "TipCredit.Approved",
    Cancelled: "TipCredit.Cancelled",
    Created: "TipCredit.Created",
    Failed: "TipCredit.Failed",
    Paid: "TipCredit.Paid",
  };

  const key = statusKeyByStatus[status];
  return key ? t(key) : status;
}

export function getStatusIconName(status: string): string {
  if (status === "Approved" || status === "Paid") return "v2/small-checkmark";
  if (status === "Failed") return "v2/warning-triangle";
  if (status === "Cancelled") return "v2/cross";
  return "v2/info";
}

export function getStatusIconColor(status: string): ColorV2 {
  if (status === "Approved" || status === "Paid") {
    return ColorV2.PositiveGreen;
  }

  if (status === "Failed") return ColorV2.NegativeRed;
  return ColorV2.Blue4;
}

export function getReferenceLabel({
  transaction,
}: GetReferenceLabelParams): string {
  return transaction.reference || "Simflexity";
}

export function sortTransactionsDescending({
  transactions,
}: TransactionSortParams): TransactionItem[] {
  return [...transactions].sort(
    (firstTransaction, secondTransaction) =>
      new Date(secondTransaction.createdAt).getTime() -
      new Date(firstTransaction.createdAt).getTime(),
  );
}
